import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Download, X, RefreshCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PWAUpdateModalProps {
  show: boolean;
  onUpdate: () => void;
  onClose: () => void;
}

export const PWAUpdateModal: React.FC<PWAUpdateModalProps> = ({ show, onUpdate, onClose }) => {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-[#1a2b3c]/60 backdrop-blur-md"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative overflow-hidden text-center z-10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Ambient background particles */}
            <div className="absolute inset-0 pointer-events-none opacity-5">
              <div className="absolute top-0 left-0 w-24 h-24 bg-emerald-500 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-blue-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            </div>

            <div className="relative z-10 flex flex-col items-center gap-6">
              <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 shadow-inner">
                <Sparkles size={40} className="animate-pulse" />
              </div>

              <div className="space-y-2">
                <h3 className="text-2xl font-black text-[#1a2b3c] tracking-tight leading-tight">{t('pwa.update_available')}</h3>
                <p className="text-sm font-medium text-gray-500 leading-relaxed px-4">
                  {t('pwa.new_version')}
                </p>
              </div>

              <div className="flex flex-col w-full gap-3 mt-2">
                <button 
                  onClick={onUpdate}
                  className="w-full bg-[#1a2b3c] text-white py-4 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-[#1a2b3c]/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2 group"
                >
                  <RefreshCcw size={16} className="group-hover:rotate-180 transition-transform duration-700" />
                  {t('pwa.upgrade')}
                </button>
                <button 
                  onClick={onClose}
                  className="w-full py-4 text-gray-400 font-black text-xs uppercase tracking-widest hover:text-[#1a2b3c] transition-colors"
                >
                  {t('pwa.maybe_later')}
                </button>
              </div>
            </div>

            <button 
              onClick={onClose}
              className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-gray-300 hover:text-gray-500 transition-all z-50 rounded-full hover:bg-gray-50"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
